import { useState, useEffect } from "react";
import { X, Plus, Minus, MessageSquare, ChevronDown, ChevronUp } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { usePublicProductAddons, ProductAddon, ProductAddonGroup } from "@/hooks/usePublicProductAddons";
import type { PublicProduct } from "@/hooks/usePublicMenu";

export interface SelectedAddon {
  id: string;
  name: string;
  price: number;
}

interface ProductDetailSheetProps {
  product: PublicProduct | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddToCart: (product: PublicProduct, quantity: number, notes: string, addons: SelectedAddon[]) => void;
}

export function ProductDetailSheet({
  product,
  open,
  onOpenChange,
  onAddToCart,
}: ProductDetailSheetProps) {
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState("");
  const [notesOpen, setNotesOpen] = useState(false);
  const [selectedAddons, setSelectedAddons] = useState<Map<string, SelectedAddon>>(new Map());

  const { data: addonGroups = [], isLoading: isLoadingAddons } = usePublicProductAddons(product?.id || null);

  // Reset state when product changes
  useEffect(() => {
    if (open && product) {
      setQuantity(1);
      setNotes("");
      setNotesOpen(false);
      setSelectedAddons(new Map());
    }
  }, [open, product?.id]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(price);
  };

  const toggleAddon = (group: ProductAddonGroup, addon: ProductAddon) => {
    setSelectedAddons((prev) => {
      const newMap = new Map(prev);
      const key = addon.id;

      if (newMap.has(key)) {
        newMap.delete(key);
      } else {
        // Check max selections for this group
        const currentGroupSelections = Array.from(newMap.values()).filter(
          (a) => addonGroups.find((g) => g.addons.some((ad) => ad.id === a.id))?.id === group.id
        );

        if (currentGroupSelections.length >= group.maxSelections) {
          // Remove first selection and add new one
          const firstInGroup = currentGroupSelections[0];
          if (firstInGroup) {
            newMap.delete(firstInGroup.id);
          }
        }

        newMap.set(key, {
          id: addon.id,
          name: addon.name,
          price: addon.price,
        });
      }

      return newMap;
    });
  };

  const isAddonSelected = (addonId: string) => selectedAddons.has(addonId);

  const getGroupSelectionCount = (group: ProductAddonGroup) => {
    return Array.from(selectedAddons.values()).filter((a) =>
      group.addons.some((ad) => ad.id === a.id)
    ).length;
  };

  const addonsTotal = Array.from(selectedAddons.values()).reduce((sum, a) => sum + a.price, 0);
  const itemTotal = product ? (product.price + addonsTotal) * quantity : 0;

  const canAddToCart = () => {
    // Check required groups
    for (const group of addonGroups) {
      if (group.isRequired) {
        const count = getGroupSelectionCount(group);
        if (count < group.minSelections) return false;
      }
    }
    return true;
  };

  const handleAddToCart = () => {
    if (!product) return;
    
    onAddToCart(
      product,
      quantity,
      notes,
      Array.from(selectedAddons.values())
    );
    onOpenChange(false);
  };

  if (!product) return null;

  const hasAddons = addonGroups.length > 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        side="bottom" 
        className="h-[90vh] rounded-t-2xl p-0 flex flex-col"
      >
        {/* Header image */}
        {product.image_url && (
          <div className="relative h-48 flex-shrink-0">
            <img
              src={product.image_url}
              alt={product.name}
              className="w-full h-full object-cover"
            />
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-3 right-3 bg-black/50 hover:bg-black/70 text-white rounded-full"
              onClick={() => onOpenChange(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 space-y-4">
          <SheetHeader className="text-left">
            <SheetTitle className="text-xl">{product.name}</SheetTitle>
            {product.description && (
              <p className="text-sm text-muted-foreground">{product.description}</p>
            )}
            <p className="text-lg font-semibold text-primary">{formatPrice(product.price)}</p>
          </SheetHeader>

          {/* Addon Groups */}
          {hasAddons && !isLoadingAddons && (
            <div className="space-y-4">
              {addonGroups.map((group) => (
                <div key={group.id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-sm">{group.name}</h3>
                      <p className="text-xs text-muted-foreground">
                        {group.isRequired
                          ? `Escolha ${group.minSelections === group.maxSelections ? group.minSelections : `de ${group.minSelections} a ${group.maxSelections}`}`
                          : `Selecione até ${group.maxSelections}`}
                      </p>
                    </div>
                    {group.isRequired && (
                      <span className="text-xs bg-destructive/10 text-destructive px-2 py-0.5 rounded">
                        Obrigatório
                      </span>
                    )}
                  </div>

                  <div className="space-y-2">
                    {group.addons.map((addon) => (
                      <label
                        key={addon.id}
                        className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                          isAddonSelected(addon.id)
                            ? "border-primary bg-primary/5"
                            : "border-border hover:bg-muted/50"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={isAddonSelected(addon.id)}
                            onCheckedChange={() => toggleAddon(group, addon)}
                          />
                          <span className="text-sm">{addon.name}</span>
                        </div>
                        <span className="text-sm font-medium text-primary">
                          + {formatPrice(addon.price)}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Notes */}
          <Collapsible open={notesOpen} onOpenChange={setNotesOpen}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="w-full justify-between px-0">
                <span className="flex items-center gap-2 text-sm">
                  <MessageSquare className="h-4 w-4" />
                  Observação
                </span>
                {notesOpen ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <Textarea
                placeholder="Ex: Sem cebola, bem passado..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="mt-2"
                rows={3}
              />
            </CollapsibleContent>
          </Collapsible>
        </div>

        {/* Footer */}
        <div className="border-t p-4 space-y-3 flex-shrink-0">
          {/* Quantity control */}
          <div className="flex items-center justify-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              disabled={quantity <= 1}
            >
              <Minus className="h-4 w-4" />
            </Button>
            <span className="text-xl font-semibold w-12 text-center">{quantity}</span>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setQuantity(quantity + 1)}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          {/* Add button */}
          <Button
            className="w-full h-12 text-base"
            onClick={handleAddToCart}
            disabled={!canAddToCart()}
          >
            Adicionar • {formatPrice(itemTotal)}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
